/**
 * Retouren v3 (sessie 172): een Picqer-retour zonder aanmelding aan de
 * voorgestelde klant koppelen, aan een open zaak of een nieuwe zaak.
 * Er gaat niets naar buiten; de retour hangt daarna bij de klant.
 */
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { vertaalApiFout } from "@/lib/api";
import { retourenV3Api, type RetourV3Dossier } from "@/lib/retouren-v3-api";

import "./dossier-v3.css";

const NIEUW = "nieuw";

interface Props {
  dossier: RetourV3Dossier;
  opNieuw: (dossier: RetourV3Dossier) => void;
  onSluiten: () => void;
}

export function KoppelKlant({ dossier, opNieuw, onSluiten }: Props) {
  const voorstel = dossier.klant_voorstel;
  const [keuze, setKeuze] = useState<string>(NIEUW);
  const [fout, setFout] = useState<string | null>(null);

  const koppel = useMutation({
    mutationFn: (klantId: string) =>
      retourenV3Api.koppelKlant(dossier.retour_id, {
        klant_id: klantId,
        zaak_id: keuze === NIEUW ? null : keuze,
      }),
    onSuccess: (nieuw) => {
      setFout(null);
      opNieuw(nieuw);
      onSluiten();
    },
    onError: (e) => setFout(vertaalApiFout(e, "Koppelen is niet gelukt.")),
  });

  if (!voorstel) {
    return (
      <div className="rv3d-paneel" data-testid="rv3d-koppel">
        <p>Geen klant gevonden bij deze order.</p>
        <div className="rv3d-paneel-knoppen">
          <button type="button" className="rv3d-knop rv3d-knop-klein" onClick={onSluiten}>
            Sluiten
          </button>
        </div>
      </div>
    );
  }

  const naam = `koppel-${dossier.retour_id}`;
  return (
    <div className="rv3d-paneel" data-testid="rv3d-koppel">
      <p>
        <strong>{voorstel.naam}</strong>, gevonden via{" "}
        {voorstel.bron === "order" ? "de order" : "het orderadres"}
      </p>
      <fieldset className="rv3d-keuzes" style={{ border: 0, margin: 0, padding: 0 }}>
        <legend className="rv3d-veldlabel" style={{ padding: 0, marginBottom: 4 }}>
          Bij welke zaak
        </legend>
        {voorstel.open_zaken.map((zaak) => (
          <label key={zaak.zaak_id}>
            <input
              type="radio"
              name={naam}
              value={zaak.zaak_id}
              checked={keuze === zaak.zaak_id}
              onChange={() => setKeuze(zaak.zaak_id)}
            />
            <span>
              {zaak.onderwerp} <span className="rv3d-melding">{zaak.laatst}</span>
            </span>
          </label>
        ))}
        <label>
          <input
            type="radio"
            name={naam}
            value={NIEUW}
            data-testid="rv3d-koppel-nieuw"
            checked={keuze === NIEUW}
            onChange={() => setKeuze(NIEUW)}
          />
          <span>Nieuwe zaak</span>
        </label>
      </fieldset>
      <div className="rv3d-paneel-knoppen">
        <button
          type="button"
          className="rv3d-knop rv3d-knop-klein"
          data-testid="rv3d-koppel-bevestig"
          disabled={koppel.isPending}
          onClick={() => koppel.mutate(voorstel.klant_id)}
        >
          {koppel.isPending ? "Bezig…" : `Koppel aan ${voorstel.naam}`}
        </button>
        <button type="button" className="rv3d-knop rv3d-knop-klein" onClick={onSluiten}>
          Annuleren
        </button>
      </div>
      {fout ? (
        <div className="rv3d-fout" role="alert">
          {fout}
        </div>
      ) : null}
    </div>
  );
}
